import {Chip} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {addToast} from "@heroui/toast";
import {Icon} from "@iconify/react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute} from '@tanstack/react-router';
import React, {useEffect, useRef, useState} from "react";
import {apiAction, apiActionImproved, apiRetrieveImproved} from "../../../api";
import ChatAttachment, {type Attachment, uploadChatFile} from "../../../components/ChatAttachment.tsx";
import Header from "../../../Header.tsx";
import {playChatChime} from "../../../utilities/chatSound";

export const Route = createFileRoute('/_app/master/support')({
    component: RouteComponent,
});

type Thread = {
    uid: string; channel: 'whatsapp' | 'portal'; thread_no: number; wa_number: string | null; contact_name: string | null; user_uid: string | null;
    club_name: string | null; status: 'open' | 'closed'; unread_count: number; user_typing: boolean; preview: string | null;
    last_inbound_at: string | null; last_outbound_at: string | null; can_free_reply: boolean;
};
type Message = {
    uid: string; direction: 'in' | 'out'; body: string; attachment: Attachment | null; status: string | null; sent_by: string | null;
    relay_status: string | null; error: string | null; at: string | null;
};

const when = (iso: string | null) => iso ? new Date(iso).toLocaleString('en-GB', {day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'}) : '';

/**
 * Master → Support: live chat from the portal and WhatsApp threads in one list. New messages
 * chime, "…is typing" shows while the organizer writes, and files travel both ways. Polls —
 * no websocket for this yet.
 */
function RouteComponent() {
    const queryClient = useQueryClient();
    const [selected, setSelected] = useState<string | null>(null);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const [pending, setPending] = useState<Attachment | null>(null);
    const [uploading, setUploading] = useState(false);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const fileRef = useRef<HTMLInputElement | null>(null);
    const lastTypingPing = useRef(0);
    const seenUnread = useRef<number | null>(null);
    const seenIn = useRef<Record<string, number>>({});

    const threadsQuery = useQuery<{threads: Thread[]; unread: number; setup: {configured: boolean; relay_to: string | null}}>({
        queryKey:        ['master-support-threads'],
        queryFn:         () => apiRetrieveImproved('master/support-threads' as any, {}),
        refetchInterval: 5000,
    });
    const messagesQuery = useQuery<{thread: Thread; messages: Message[]}>({
        queryKey:        ['master-support-messages', selected],
        queryFn:         () => apiRetrieveImproved('master/support-messages' as any, {thread_uid: selected}),
        enabled:         !!selected,
        refetchInterval: 3000,
    });

    const threads = threadsQuery.data?.threads || [];
    const setup = threadsQuery.data?.setup;
    const current = messagesQuery.data?.thread;
    const messages = messagesQuery.data?.messages || [];
    const userTyping = !!current?.user_typing;

    useEffect(() => { bottomRef.current?.scrollIntoView({block: 'end'}); }, [messages.length, userTyping]);
    // Opening a thread reads it server-side; refresh the list so the unread badge drops.
    useEffect(() => { if (messagesQuery.data) queryClient.invalidateQueries({queryKey: ['master-support-threads']}); }, [messagesQuery.data?.thread.uid]);
    // Chime: a new message in a thread that is not open (unread total rises), or a new inbound
    // message in the open thread. Silent on first load.
    useEffect(() => {
        const n = threadsQuery.data?.unread;
        if (n === undefined) return;
        if (seenUnread.current !== null && n > seenUnread.current) playChatChime();
        seenUnread.current = n;
    }, [threadsQuery.data?.unread]);
    useEffect(() => {
        if (!current) return;
        const inCount = messages.filter((m) => m.direction === 'in').length;
        const prev = seenIn.current[current.uid];
        if (prev !== undefined && inCount > prev) playChatChime();
        seenIn.current[current.uid] = inCount;
    }, [messages.length, current?.uid]);

    const pingTyping = () => {
        if (!selected) return;
        const now = Date.now();
        if (now - lastTypingPing.current < 2500) return;
        lastTypingPing.current = now;
        apiActionImproved('master/support-typing' as any, {thread_uid: selected, typing: true}).catch(() => {});
    };

    const pickFile = async (file: File | undefined) => {
        if (!file) return;
        setUploading(true);
        try {
            setPending(await uploadChatFile(file, apiAction as any));
        } catch (e: any) {
            addToast({title: e?.message || 'The file did not upload', color: 'danger'});
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const send = async () => {
        const body = draft.trim();
        if ((!body && !pending) || !selected) return;
        setSending(true);
        try {
            await apiActionImproved('master/support-reply' as any, {thread_uid: selected, body, attachment: pending});
            setDraft('');
            setPending(null);
            queryClient.invalidateQueries({queryKey: ['master-support-messages', selected]});
            queryClient.invalidateQueries({queryKey: ['master-support-threads']});
        } catch (e: any) {
            addToast({title: e?.message || 'The reply was not sent', color: 'danger'});
        } finally {
            setSending(false);
        }
    };
    const toggleClosed = async () => {
        if (!current) return;
        await apiActionImproved('master/support-close' as any, {thread_uid: current.uid, reopen: current.status === 'closed'});
        queryClient.invalidateQueries({queryKey: ['master-support-messages', selected]});
        queryClient.invalidateQueries({queryKey: ['master-support-threads']});
    };

    return (
        <>
            <Header />
            <div className="mx-auto w-[98%] max-w-6xl py-4">
                <div className="mb-1 flex items-center justify-between gap-3">
                    <h1 className="text-2xl font-bold">Support</h1>
                    {setup && (
                        <span className="text-xs text-default-400">
                            Portal chat on{setup.configured ? ' · WhatsApp connected' : ' · WhatsApp not connected yet'}
                            {setup.relay_to ? ` · copies go to +${setup.relay_to}` : ''}
                        </span>
                    )}
                </div>
                <p className="mb-4 text-sm text-default-500">
                    Live chat from the portal's "Chat with us" button lands here; reply and the organizer sees it in
                    their chat panel. WhatsApp threads appear here too once the number is connected.
                </p>

                {threadsQuery.isLoading ? (
                    <div className="flex justify-center py-12"><Spinner size="lg" /></div>
                ) : threads.length === 0 ? (
                    <div className="rounded-xl border border-default-200 bg-content1 py-12 text-center text-default-500">
                        No conversations yet. They appear here the moment someone messages support.
                    </div>
                ) : (
                    <div className="grid gap-3 md:grid-cols-[300px_1fr]" style={{minHeight: 520}}>
                        <div className="overflow-y-auto rounded-xl border border-default-200 bg-content1" style={{maxHeight: 640}}>
                            {threads.map((t) => (
                                <button key={t.uid} type="button" onClick={() => setSelected(t.uid)}
                                        className={'block w-full border-b border-default-100 px-3 py-2.5 text-left transition hover:bg-default-50 ' + (selected === t.uid ? 'bg-primary/5' : '')}>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="truncate text-sm font-medium">
                                            <span className="text-default-400">#{t.thread_no}</span> {t.contact_name || (t.wa_number ? '+' + t.wa_number : 'Portal user')}
                                        </span>
                                        {t.unread_count > 0 && <Chip size="sm" color="primary" variant="solid">{t.unread_count}</Chip>}
                                    </div>
                                    <div className="truncate text-xs text-default-500">
                                        <span className={'mr-1 rounded px-1 text-[10px] uppercase ' + (t.channel === 'portal' ? 'bg-primary/10 text-primary' : 'bg-success/10 text-success')}>
                                            {t.channel === 'portal' ? 'Portal' : 'WhatsApp'}
                                        </span>
                                        {t.club_name || (t.user_uid ? 'ScorePal account' : 'Unknown number')}
                                    </div>
                                    <div className="truncate text-xs text-default-400">{t.user_typing ? <span className="italic text-primary">typing…</span> : t.preview}</div>
                                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-default-400">
                                        <span>{when(t.last_inbound_at || t.last_outbound_at)}</span>
                                        {t.status === 'closed' && <span className="rounded bg-default-100 px-1">closed</span>}
                                    </div>
                                </button>
                            ))}
                        </div>

                        <div className="flex flex-col rounded-xl border border-default-200 bg-content1">
                            {!selected ? (
                                <div className="flex flex-1 items-center justify-center text-sm text-default-400">Pick a conversation</div>
                            ) : messagesQuery.isLoading || !current ? (
                                <div className="flex flex-1 items-center justify-center"><Spinner size="sm" /></div>
                            ) : (
                                <>
                                    <div className="flex items-center justify-between gap-2 border-b border-default-100 px-4 py-2.5">
                                        <div>
                                            <div className="text-sm font-semibold">#{current.thread_no} {current.contact_name || (current.wa_number ? '+' + current.wa_number : 'Portal user')}</div>
                                            <div className="text-xs text-default-500">
                                                {current.channel === 'portal' ? 'Portal chat' : `+${current.wa_number}`}{current.club_name ? ` · ${current.club_name}` : ''}
                                                {current.user_uid && <a className="ml-2 text-primary hover:underline" href={`/profiles/${current.user_uid}/`} target="_blank" rel="noreferrer">profile</a>}
                                                {userTyping && <span className="ml-2 italic text-primary">typing…</span>}
                                            </div>
                                        </div>
                                        <button type="button" onClick={toggleClosed} className="text-xs text-default-500 hover:underline">
                                            {current.status === 'closed' ? 'Reopen' : 'Close'}
                                        </button>
                                    </div>
                                    <div className="flex-1 space-y-2 overflow-y-auto px-4 py-3" style={{maxHeight: 460}}>
                                        {messages.map((m) => (
                                            <div key={m.uid} className={'flex ' + (m.direction === 'out' ? 'justify-end' : 'justify-start')}>
                                                <div className={'max-w-[78%] rounded-2xl px-3 py-2 text-sm ' + (m.direction === 'out' ? 'bg-primary text-white' : 'bg-default-100')}>
                                                    {m.body && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
                                                    {m.attachment && <ChatAttachment attachment={m.attachment} onDark={m.direction === 'out'} />}
                                                    <div className={'mt-0.5 text-[10px] ' + (m.direction === 'out' ? 'text-white/70' : 'text-default-400')}>
                                                        {when(m.at)}
                                                        {m.direction === 'out' && (m.sent_by === 'relay' ? ' · from phone' : m.sent_by === 'auto' ? ' · automatic' : ' · from desk')}
                                                        {m.status === 'failed' && <span className="ml-1 text-danger-300">· not delivered{m.error ? `: ${m.error}` : ''}</span>}
                                                        {m.direction === 'in' && m.relay_status === 'failed' && <span className="ml-1 text-danger">· copy to phone failed</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        {userTyping && (
                                            <div className="flex justify-start">
                                                <div className="rounded-2xl bg-default-100 px-3 py-2 text-sm" aria-label="Typing">
                                                    <span className="inline-flex gap-1 align-middle">
                                                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-default-400" style={{animationDelay: '0ms'}} />
                                                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-default-400" style={{animationDelay: '150ms'}} />
                                                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-default-400" style={{animationDelay: '300ms'}} />
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        <div ref={bottomRef} />
                                    </div>
                                    <div className="border-t border-default-100 p-3">
                                        {!current.can_free_reply && (
                                            <div className="mb-2 text-xs text-warning-600">
                                                They last wrote more than 24 hours ago. WhatsApp only lets us send an approved template until they message again.
                                            </div>
                                        )}
                                        {pending && (
                                            <div className="mb-2 flex items-center gap-2 text-xs">
                                                <Icon icon="mdi:paperclip" className="text-base text-default-500" />
                                                <span className="truncate">{pending.name}</span>
                                                <button type="button" className="text-default-400 hover:text-danger" onClick={() => setPending(null)} aria-label="Remove attachment">
                                                    <Icon icon="mdi:close" />
                                                </button>
                                            </div>
                                        )}
                                        <div className="flex items-end gap-2">
                                            <input ref={fileRef} type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.mp4,.mov"
                                                   onChange={(e) => pickFile(e.target.files?.[0])} />
                                            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} aria-label="Attach a file"
                                                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-default-500 hover:bg-default-100 disabled:opacity-40">
                                                {uploading ? <Spinner size="sm" /> : <Icon icon="mdi:paperclip" className="text-xl" />}
                                            </button>
                                            <textarea
                                                value={draft}
                                                onChange={(e) => { setDraft(e.target.value); if (e.target.value) pingTyping(); }}
                                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                                                placeholder={(current.channel === 'portal' ? 'Reply in their chat panel…' : 'Reply on WhatsApp…') + ' (Enter to send, Shift+Enter for a new line)'}
                                                rows={2}
                                                className="w-full resize-none rounded-lg border border-default-200 bg-content2 px-3 py-2 text-sm outline-none focus:border-primary"
                                            />
                                            <button type="button" onClick={send} disabled={sending || (!draft.trim() && !pending)} aria-label="Send reply"
                                                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-white disabled:opacity-40">
                                                <Icon icon="mdi:send" className="text-xl" />
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
